// code-guardian-ignore-duplicates: shared presentational scaffolding (table/modal/form/report layout from the design system)
import ReportPageScaffold from '../components/ReportPageScaffold';
import PaginationFooter from '../components/PaginationFooter';
import { ReportLoadingState } from '../components/ReportLoadingState';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Download, SlidersHorizontal } from 'lucide-react';
import { attendanceAPI } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import ImageLightbox from '../components/ImageLightbox';
import CustomReportPanel from '../components/CustomReportPanel';
import { PAGE_SIZE_OPTIONS } from '../constants/reportPageSize';
import { buildCsv, downloadCsv } from '../utils/csv';
import { useLocation } from 'react-router-dom';
import { readDrillParams } from '../utils/drilldown';
import { getLocalYesterdayDateString } from '../utils/dateHelpers';
import { usePagination } from '../hooks/usePagination';
import { useReportFilters } from '../hooks/useReportFilters';
import { useAddressEntry } from '../hooks/useLinkedSearch';
import DateRangeFilter from '../components/DateRangeFilter';
import { useTableSort, SortTh, type SortAccessors } from '../components/dashboard/useTableSort';
import DashboardFilterModal from '../components/dashboard/DashboardFilterModal';
import { useStoreAttrFilters } from '../hooks/useStoreAttrFilters';

interface AttendanceRecord {
  employeeCode: string;
  employeeName: string;
  /** Reporting manager, resolved server-side. null when 1Office can't resolve the hop. */
  reportingManagerName?: string | null;
  reportingManagerCode?: string | null;
  roleCategory?: string;
  storeName: string;
  storeCode: string;
  attendanceDate: string;
  date: string;
  inTime: string | null;
  signInTime: string | null;
  outTime: string | null;
  signOutTime: string | null;
  workingHours: string;
  attendanceStatus: number;
  status: number;
  // Composite half-day label from the backend ("Half Day Present + Half Day Sick
  // Leave"); falls back to the single-label badge when absent.
  statusLabel?: string | null;
  leaveType: string | null;
  leaveDays: string | null;
  regularizationType?: string | null;
  regularizationComments?: string | null;
  signInSelfiePath: string | null;
  signOutSelfiePath: string | null;
  signInLocation: string | null;
  signOutLocation: string | null;
  location: string;
  locationLink: string;
  latitude?: number;
  longitude?: number;
  address: string;
  city: string;
  state: string;
  /** Store attributes used by the Trade / Category / Region / City filters. */
  trade?: string | null;
  storeCategory?: string | null;
}

interface AttendanceResponse {
  success: boolean;
  data: AttendanceRecord[];
  meta: {
    fromDate: string;
    toDate: string;
    totalRecords: number;
    isActive: boolean;
    /** The code the server narrowed to when asked for one person (`onlyEmployee`),
     *  as it matched it; null when it was asked for everyone. */
    onlyEmployee?: string | null;
  };
}

// ── One-person load ──────────────────────────────────────────────────────────
// A range for everyone in the viewer's scope is one large ask of 1Office (hundreds of
// people × every day), and the search box only narrows the rows already on screen. So
// when the box holds exactly ONE employee code at the moment the page loads (Apply,
// opening the page, a link naming a person), the server is asked for that person only.
//
// One employee code = one token of 3–40 letters, digits, '.', '_' or '-' holding at
// least one digit: the shape codes have, and what the server accepts. A name, a word
// with no digit ('EMP') or two words never counts, and loads everyone as before. A
// token that is only PART of a code ('3375') does count; the server then finds nobody,
// and the page loads everyone and filters on screen (see `load`), as it always did.
const EMPLOYEE_CODE_TOKEN = /^[A-Za-z0-9._-]{3,40}$/;
const employeeCodeInSearch = (text: string): string | null => {
  const token = text.trim();
  return EMPLOYEE_CODE_TOKEN.test(token) && /\d/.test(token) ? token : null;
};
const sameCode = (a: string, b: string): boolean => a.trim().toLowerCase() === b.trim().toLowerCase();

// How long the box must rest on other text before a one-person table is replaced by
// everyone's rows, so each keystroke does not start a load of its own.
const EVERYONE_RELOAD_DELAY_MS = 400;

/** One ask of the server: the applied range, and the one person when narrowed. */
interface LoadRequest {
  fromDate: string;
  toDate: string;
  projectId: number | undefined;
  onlyEmployee: string | null;
  /** The employee code the box held when this ask began — kept when the page then
   *  loads everyone instead (nobody had that code, or the box moved off it), so Apply
   *  does not keep offering a code the page has already tried. */
  boxCode: string | null;
}

// The server's own words when it sends them (it says what went wrong in plain English
// and never passes 1Office's error text on); ours when nothing usable came back.
const LOAD_FAILED = "Couldn't load attendance. Try again.";
const loadFailureMessage = (err: unknown): string => {
  const message = (err as { response?: { data?: { message?: unknown } } } | null)?.response?.data?.message;
  return typeof message === 'string' && message.trim() ? message : LOAD_FAILED;
};

const getSelfieViewUrl = (selfiePath: string | null): string | null => {
  if (!selfiePath || selfiePath.trim() === '') return null;
  // Use the common image API endpoint that handles both Azure and local storage
  return `/api/images/view/${selfiePath}`;
};

const formatTime = (timeString: string | null): string => {
  if (!timeString) return '—';
  try {
    // Parse time string (HH:mm:ss format)
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const minute = minutes;
    
    // Convert to 12-hour format with am/pm
    const period = hour >= 12 ? 'pm' : 'am';
    const hour12 = hour % 12 || 12;
    
    return `${hour12}:${minute} ${period}`;
  } catch (error) {
    return timeString;
  }
};

// Week-off can arrive either as attendanceStatus === 3 OR as a leaveType string
// ("Week Off" / "Weekoff" / "week off"). Same matcher the backend scorecard
// (apps/api/src/services/apple-scorecard.service.ts) and mobile
// (apps/mobile/src/utils/teamAttendance.ts resolveStatus) use, so all three agree.
const WEEK_OFF_RE = /week.?off/i;

type StatusCategory =
  | 'present' | 'absent' | 'half_day' | 'week_off' | 'leave' | 'holiday' | 'pending' | 'in_progress';

// The row's EFFECTIVE status — mirrors the badge's precedence (a non-empty
// leaveType overrides the numeric attendanceStatus). Used by BOTH the filter and
// the dropdown so "what you filter" matches "what the badge shows".
const resolveStatusCategory = (r: { attendanceStatus: number; leaveType: string | null }): StatusCategory => {
  const lt = (r.leaveType || '').trim();
  if (lt) return WEEK_OFF_RE.test(lt) ? 'week_off' : 'leave';
  switch (r.attendanceStatus) {
    case 1: return 'present';
    case 2: return 'half_day';
    case 3: return 'week_off';
    case 4: return 'holiday';
    case 5: return 'pending';
    case 6: return 'in_progress';
    default: return 'absent'; // 0
  }
};

// Fixed display order + labels for the status filter dropdown. "Leave" is a single
// bucket grouping every non-week-off leave type (Casual / Earned / Sick / …).
const STATUS_CATEGORY_META: Array<{ key: StatusCategory; label: string }> = [
  { key: 'present', label: 'Present' },
  { key: 'absent', label: 'Absent' },
  { key: 'half_day', label: 'Half Day' },
  { key: 'week_off', label: 'Week Off' },
  { key: 'leave', label: 'Leave' },
  { key: 'holiday', label: 'Holiday' },
  { key: 'pending', label: 'Pending' },
  { key: 'in_progress', label: 'Day in Progress' },
];

const getAttendanceStatusLabel = (status: number, leaveType: string | null): { label: string; color: string } => {
  // If there's a leave type, show the leave type instead of "Absent" — except
  // week-off variants ("Weekoff"), which are normalised to a consistent "Week Off".
  if (leaveType) {
    if (WEEK_OFF_RE.test(leaveType)) {
      return { label: 'Week Off', color: 'bg-purple-100 text-purple-800' };
    }
    return { label: leaveType, color: 'bg-yellow-100 text-yellow-800' };
  }

  const statusMap: { [key: number]: { label: string; color: string } } = {
    0: { label: 'Absent', color: 'bg-red-100 text-red-800' },
    1: { label: 'Present', color: 'bg-green-100 text-green-800' },
    2: { label: 'Half Day', color: 'bg-yellow-100 text-yellow-800' },
    3: { label: 'Week Off', color: 'bg-purple-100 text-purple-800' },
    4: { label: 'Holiday', color: 'bg-blue-100 text-blue-800' },
    5: { label: 'Pending', color: 'bg-orange-100 text-orange-800' },
    6: { label: 'Day in Progress', color: 'bg-blue-100 text-blue-800' },
  };
  return statusMap[status] || { label: 'Unknown', color: 'bg-gray-100 text-gray-800' };
};

// ── Column sorting ───────────────────────────────────────────────────────────
// Sign-in / sign-out as minutes past midnight, split exactly as formatTime reads
// the raw 'HH:mm:ss' — so the order matches the 12-hour time the cell shows.
const timeSortValue = (timeString: string | null): number | null => {
  if (!timeString) return null;
  const [hours, minutes] = timeString.split(':');
  const total = parseInt(hours, 10) * 60 + parseInt(minutes, 10);
  return Number.isNaN(total) ? null : total;
};

// Working hours arrive pre-formatted as "8h 30m" (blank → the cell's "—"), so sort
// on the minutes behind the label rather than on the text.
const workingMinutesSortValue = (workingHours: string | null | undefined): number | null => {
  const match = /(\d+)h\s*(\d+)m/.exec(workingHours || '');
  return match ? parseInt(match[1], 10) * 60 + parseInt(match[2], 10) : null;
};

// One accessor per sortable column — each returns the value its cell shows. Defined
// once at module level so the sort isn't re-run on every unrelated re-render (page
// flips, lightbox) of what can be a many-thousand-row date range.
const SORT_ACCESSORS: SortAccessors<AttendanceRecord> = {
  employeeCode: (r) => r.employeeCode,
  // The API sends the literal 'N/A' when it can't resolve a name — sink it like a blank.
  employeeName: (r) => (r.employeeName === 'N/A' ? null : r.employeeName),
  storeName: (r) => r.storeName,
  storeCode: (r) => r.storeCode,
  date: (r) => r.attendanceDate, // ISO 'YYYY-MM-DD…' → sorts chronologically as text
  signInTime: (r) => timeSortValue(r.inTime),
  signOutTime: (r) => timeSortValue(r.outTime),
  workingHours: (r) => workingMinutesSortValue(r.workingHours),
  status: (r) => getAttendanceStatusLabel(r.attendanceStatus, r.leaveType).label,
  detailedStatus: (r) => r.statusLabel || getAttendanceStatusLabel(r.attendanceStatus, r.leaveType).label,
};

export default function DailyAttendanceLogsPage() {
  const { selectedProjectId } = useAuthStore();
  const [attendanceData, setAttendanceData] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasFetchedRef = useRef(false);
  // Every load takes the next number, and a reply is used only while its number is
  // still the latest: a slow earlier answer must never replace a newer one.
  const requestSeq = useRef(0);
  // The latest ask of the server — what Try again repeats, unchanged, and what Apply
  // compares the box with.
  const [lastRequest, setLastRequest] = useState<LoadRequest | null>(null);
  // Set while the table holds ONE person's rows because the server was asked for only
  // them: their code as the server matched it, and the ask that loaded them.
  const [narrowed, setNarrowed] = useState<{ code: string; request: LoadRequest } | null>(null);

  // Filters - Default to previous day for Daily Attendance (current day data not available)
  // Uses the shared getLocalYesterdayDateString helper (same local-time
  // YYYY-MM-DD output the other report pages land on).
  // Date inputs use a "pending → applied" model so editing the date does not
  // auto-fire the heavy attendance query. Only Apply (or the initial load /
  // a project switch) commits the pending range to `appliedFromDate` /
  // `appliedToDate`, which is what the fetch effect depends on. This is the
  // same filter + pagination state block the other report pages share verbatim
  // — see hooks/useReportFilters. The hook's generic start/end naming is
  // renamed here to this page's local from/to convention.
  // DEEP LINK (dashboard drill-down): ?employeeCode=&startDate=&endDate=
  // Parsed ONCE and fed to useReportFilters' INITIALIZERS rather than pushed in via a
  // mount effect. The hook seeds pending AND applied from the same initial values, so a
  // single render lands both — Apply stays correctly disabled and the mount fetch runs
  // exactly once with the linked range. A setter-in-effect would instead fetch the
  // default range first, then refetch, and leave a phantom "pending changes" state.
  const location = useLocation();
  const drill = useMemo(() => readDrillParams(location.search), [location.search]);

  const {
    startDate: fromDate, setStartDate: setFromDate,
    endDate: toDate, setEndDate: setToDate,
    appliedStartDate: appliedFromDate,
    appliedEndDate: appliedToDate,
    searchQuery, setSearchQuery,
    pageSize, setPageSize,
    currentPage, setCurrentPage,
    hasPendingDateChanges,
    applyDateRange,
  } = useReportFilters({
    initialStartDate: drill.startDate || getLocalYesterdayDateString(),
    initialEndDate: drill.endDate || getLocalYesterdayDateString(),
    // The page's only employee filter is the free-text search box, so an incoming
    // employee code prefills it (matches on code as well as name).
    initialSearchQuery: drill.employeeCode || drill.q || '',
  });
  const [statusFilter, setStatusFilter] = useState('');
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  // A link landing on the page while it is open (search picks another person in the
  // same tab, so nothing remounts) is a fresh open of that address: useReportFilters has
  // already put its text in the box. When that text is one employee code, the page loads
  // again for that person, as opening the link would — rather than swapping the previous
  // person's rows for everyone's. Counted in render, like the hook's own re-read, so a
  // link that also changes the dates is still ONE load. Other arrivals load nothing new.
  const entry = useAddressEntry();
  const [linkArrival, setLinkArrival] = useState(() => ({ key: entry?.key, count: 0 }));
  if (entry && linkArrival.key !== entry.key) {
    const namesOnePerson = entry.arrived && employeeCodeInSearch(drill.employeeCode || drill.q) !== null;
    setLinkArrival({ key: entry.key, count: linkArrival.count + (namesOnePerson ? 1 : 0) });
  }

  const load = useCallback(async (request: LoadRequest): Promise<void> => {
    const id = ++requestSeq.current;
    setLastRequest(request);
    setLoading(true);
    setError(null);
    setNarrowed(null);
    try {
      const response = await attendanceAPI.getDailyAttendanceLogs({
        fromDate: request.fromDate,
        toDate: request.toDate,
        isActive: true,
        projectId: request.projectId,
        ...(request.onlyEmployee ? { onlyEmployee: request.onlyEmployee } : {}),
      });
      if (id !== requestSeq.current) return;

      const data = response.data as AttendanceResponse;
      const rows = Array.isArray(data?.data) ? data.data : [];
      if (request.onlyEmployee && rows.length === 0) {
        // Nobody has exactly that code — most often part of one ('3375'), or a store
        // code. Load everyone and let the box filter on screen, as it always did.
        void load({ ...request, onlyEmployee: null });
        return;
      }
      setAttendanceData(rows);
      const matched = request.onlyEmployee ? data.meta?.onlyEmployee : null;
      if (matched && rows.every((r) => sameCode(r.employeeCode ?? '', matched))) {
        setNarrowed({ code: matched, request });
      }
    } catch (err) {
      if (id !== requestSeq.current) return;
      // Never leave the previous answer's rows under the error: they read as this one's.
      setAttendanceData([]);
      setError(loadFailureMessage(err));
      console.error('Error fetching attendance logs:', err);
    } finally {
      if (id === requestSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    hasFetchedRef.current = true;
    const code = employeeCodeInSearch(searchQuery);
    void load({
      fromDate: appliedFromDate,
      toDate: appliedToDate,
      projectId: selectedProjectId ?? undefined,
      onlyEmployee: code,
      boxCode: code,
    });
    // The box is READ when a load starts, not watched: typing filters on screen, and
    // only Apply, opening the page, a project switch or a link naming a person loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId, appliedFromDate, appliedToDate, linkArrival.count]);

  // A one-person table must never sit under a search for someone else: once the box
  // rests on anything but that code, load everyone for the same range again.
  useEffect(() => {
    if (!narrowed || sameCode(searchQuery, narrowed.code)) return;
    const timer = setTimeout(() => {
      void load({ ...narrowed.request, onlyEmployee: null });
    }, EVERYONE_RELOAD_DELAY_MS);
    return () => clearTimeout(timer);
  }, [narrowed, searchQuery, load]);

  const retryLoad = () => {
    if (lastRequest) void load(lastRequest);
  };

  // Apply also loads ONE person for the applied range when the box holds an employee
  // code the page has not asked for, and everyone's rows are not on screen to filter
  // (the everyone-load failed or is still running, or another person is narrowed).
  // Without this, Apply needed a date change, so after a failed everyone-load nobody
  // could ask for just the person they wanted. With everyone shown, typing a code
  // filters on screen as before and Apply stays off.
  const codeInBox = employeeCodeInSearch(searchQuery);
  const everyoneShown = !loading && !error && lastRequest !== null && lastRequest.onlyEmployee === null;
  const codePending = codeInBox !== null
    && lastRequest !== null // the first load is about to read the box itself
    && !everyoneShown
    && !(lastRequest.boxCode !== null && sameCode(codeInBox, lastRequest.boxCode));
  const canApply = hasPendingDateChanges || codePending;
  const applyFilters = () => {
    // New dates: the load effect runs and reads the box itself.
    if (hasPendingDateChanges) { applyDateRange(); return; }
    if (!codePending) return;
    setCurrentPage(1);
    void load({
      fromDate: appliedFromDate,
      toDate: appliedToDate,
      projectId: selectedProjectId ?? undefined,
      onlyEmployee: codeInBox,
      boxCode: codeInBox,
    });
  };

  // Effective status categories present in the data, in fixed display order.
  // Built from resolveStatusCategory (not the raw numeric status) so the dropdown
  // offers "Leave" and a single "Week Off" that match the badges.
  const availableStatusCategories = useMemo(() => {
    const present = new Set<StatusCategory>();
    attendanceData.forEach(record => present.add(resolveStatusCategory(record)));
    return STATUS_CATEGORY_META.filter(c => present.has(c.key));
  }, [attendanceData]);

  // Filter data based on search query, status, and category
  // Store-attribute filters (Trade / Category / Region / City), shared with the other
  // report pages so a store scopes the same way everywhere. Region is derived from the
  // state; a report whose rows lack an attribute simply doesn't offer that filter.
  const [storeFiltersOpen, setStoreFiltersOpen] = useState(false);
  const storeFilters = useStoreAttrFilters(attendanceData, {
    trade: (r) => r.trade,
    category: (r) => r.storeCategory,
    city: (r) => r.city,
    state: (r) => r.state,
  });

  const filteredData = useMemo(() => attendanceData.filter(record => {
    const matchesSearch = !searchQuery ||
      record.employeeName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      record.employeeCode.toLowerCase().includes(searchQuery.toLowerCase()) ||
      record.storeName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      record.storeCode.toLowerCase().includes(searchQuery.toLowerCase()) ||
      record.city.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = !statusFilter || resolveStatusCategory(record) === statusFilter;

    return matchesSearch && matchesStatus && storeFilters.matches(record);
    // `matches` is stable per filter selection (the object around it is not).
  }), [attendanceData, searchQuery, statusFilter, storeFilters.matches]);

  // Snap back to page 1 when the filtered set changes — otherwise the user
  // narrows the result with a search and lands on an empty page N.
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, storeFilters.activeCount, pageSize]);

  // Column sorting runs on the WHOLE filtered set, then pagination slices the
  // result. A new sort order starts from page 1.
  const sort = useTableSort(filteredData, SORT_ACCESSORS);
  useEffect(() => {
    setCurrentPage(1);
  }, [sort.sortKey, sort.sortDir]);

  const { totalPages, safePage, paginatedData } = usePagination(sort.sorted, currentPage, pageSize);

  const handleExportCSV = () => {
    // While a load runs the rows held are the previous answer's (hidden under the
    // loading state) — never export those as this one.
    if (loading) return;
    const headers = [
      'Employee Code',
      'Employee Name',
      'Reporting Manager',
      'Reporting Manager Code',
      'Store Name',
      'Store Code',
      'Date',
      'Sign-In Time',
      'Sign-Out Time',
      'Working Hours',
      'Status',
      'Detailed Status',
      'Type',
      'Sign-In Location',
      'Sign-Out Location',
      'Sign-In Selfie',
      'Sign-Out Selfie'
    ];

    const getFullImageUrl = (path: string | null): string => {
      if (!path || path.trim() === '') return '-';
      return `${window.location.origin}/api/images/view/${path}`;
    };
    
    const csvData = filteredData.map(record => [
      record.employeeCode,
      record.employeeName,
      record.reportingManagerName || '-',
      record.reportingManagerCode || '-',
      record.storeName || '-',
      record.storeCode || '-',
      record.attendanceDate,
      record.inTime ? formatTime(record.inTime) : '-',
      record.outTime ? formatTime(record.outTime) : '-',
      record.workingHours || '-',
      getAttendanceStatusLabel(record.attendanceStatus, record.leaveType).label,
      record.statusLabel || getAttendanceStatusLabel(record.attendanceStatus, record.leaveType).label,
      record.regularizationType || 'Regular',
      record.signInLocation || '-',
      record.signOutLocation || '-',
      getFullImageUrl(record.signInSelfiePath),
      getFullImageUrl(record.signOutSelfiePath)
    ]);

    downloadCsv(`attendance-logs-${fromDate}-to-${toDate}.csv`, buildCsv(headers, csvData));
  };

  return (
    <CustomReportPanel
      sourceKey="daily-attendance"
      sourceLabel="Daily Attendance"
      promptPlaceholder='e.g. "Top 10 stores by check-in count this month, as a bar chart"'
    >
    <ReportPageScaffold
      title="Daily Attendance"
      searchValue={searchQuery}
      onSearchChange={(e) => setSearchQuery(e.target.value)}
      searchPlaceholder="Search by name, emp code, email, or city..."
      filters={
        <>
          <DateRangeFilter
            startDate={fromDate}
            endDate={toDate}
            hasPendingDateChanges={canApply}
            onStartDateChange={(e) => setFromDate(e.target.value)}
            onEndDateChange={(e) => setToDate(e.target.value)}
            onStartDateKeyDown={(e) => { if (e.key === 'Enter' && canApply) applyFilters(); }}
            onEndDateKeyDown={(e) => { if (e.key === 'Enter' && canApply) applyFilters(); }}
            onApply={applyFilters}
          />

          <button
            type="button"
            onClick={() => setStoreFiltersOpen(true)}
            className={`flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm font-medium shadow-sm transition ${
              storeFilters.activeCount > 0
                ? 'border-primary-200 bg-primary-50 text-primary-700'
                : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
            }`}
            title="Filter by store trade, category, region or city"
          >
            <SlidersHorizontal className="w-4 h-4" /> Filters
            {storeFilters.activeCount > 0 && (
              <span className="ml-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary-600 px-1.5 text-xs font-bold text-white">{storeFilters.activeCount}</span>
            )}
          </button>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Status</option>
            {availableStatusCategories.map(cat => (
              <option key={cat.key} value={cat.key}>
                {cat.label}
              </option>
            ))}
          </select>

          <button
            onClick={handleExportCSV}
            disabled={loading || filteredData.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            Download
          </button>
        </>
      }
    >
        {/* Error Message — Try again repeats the same ask (range, and the one person
            when the page had narrowed to them). */}
        {error && (
          <div role="alert" className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm mt-4 flex flex-wrap items-center justify-between gap-3">
            <span>{error}</span>
            <button
              type="button"
              onClick={retryLoad}
              className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
            >
              Try again
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && <ReportLoadingState />}

        {/* No Records Found — not under an error: nothing was loaded, so "no records"
            would be a claim about attendance the page never saw. */}
        {!loading && !error && filteredData.length === 0 && (
          <div className="flex items-center justify-center py-16">
            <p className="text-sm text-gray-500">No records found</p>
          </div>
        )}

        {/* Table */}
        {!loading && filteredData.length > 0 && (
        <div className="overflow-hidden mt-6">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50/70">
              <tr>
                <SortTh label="EMPLOYEE CODE" sortKey="employeeCode" sort={sort} align="center" className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap" />
                <SortTh label="EMPLOYEE NAME" sortKey="employeeName" sort={sort} align="center" className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap" />
                <SortTh label="STORE NAME" sortKey="storeName" sort={sort} align="center" className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap" />
                <SortTh label="STORE CODE" sortKey="storeCode" sort={sort} align="center" className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap" />
                <SortTh label="DATE" sortKey="date" sort={sort} align="center" className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap" />
                <SortTh label="SIGN-IN TIME" sortKey="signInTime" sort={sort} align="center" className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap" />
                <SortTh label="SIGN-OUT TIME" sortKey="signOutTime" sort={sort} align="center" className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap" />
                <SortTh label="WORKING HOURS" sortKey="workingHours" sort={sort} align="center" className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap" />
                <SortTh label="STATUS" sortKey="status" sort={sort} align="center" className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap" />
                <SortTh label="DETAILED STATUS" sortKey="detailedStatus" sort={sort} align="center" className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap" />
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap">
                  SIGN-IN LOCATION
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap">
                  SIGN-OUT LOCATION
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap">
                  SIGN-IN SELFIE
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap">
                  SIGN-OUT SELFIE
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedData.map((record, index) => {
                  const statusInfo = getAttendanceStatusLabel(record.attendanceStatus, record.leaveType);
                  const signInSelfieUrl = getSelfieViewUrl(record.signInSelfiePath);
                  const signOutSelfieUrl = getSelfieViewUrl(record.signOutSelfiePath);
                  
                  return (
                    <tr key={`${record.employeeCode}-${record.attendanceDate}-${index}`} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {record.employeeCode}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-center">
                        {record.employeeName || 'N/A'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {record.storeName || '—'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {record.storeCode || '—'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {record.attendanceDate}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {record.inTime ? formatTime(record.inTime) : '—'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {record.outTime ? formatTime(record.outTime) : '—'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {record.workingHours || '—'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        {(() => {
                          const type = record.regularizationType || 'Regular';
                          const isRegular = type === 'Regular';
                          const isApproved = type.includes('Approved');
                          const isRejected = type.includes('Rejected');
                          const category = type.replace(/\s*\(.*\)$/, '');
                          const approvalState = type.match(/\(([^)]+)\)/)?.[1] || '';
                          const dotCls = isApproved
                            ? 'bg-green-500'
                            : isRejected
                              ? 'bg-red-500'
                              : 'bg-orange-500';

                          // Infer attendance-regularization sub-type from leave fields
                          const hasLeaveType = !!record.leaveType;
                          const leaveDayNum = parseFloat(String(record.leaveDays ?? ''));
                          let requestSubtype: string | null = null;
                          if (!isRegular && category === 'Attendance Regularization') {
                            if (hasLeaveType && !Number.isNaN(leaveDayNum) && leaveDayNum > 0 && leaveDayNum < 0.9) {
                              requestSubtype = 'Regularize Half Day & Apply Half Day Leave';
                            } else if (hasLeaveType) {
                              requestSubtype = 'Apply Full Day Leave';
                            } else {
                              requestSubtype = 'Regularize (Full Day Present)';
                            }
                          }

                          return (
                            <div className="relative inline-block group">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusInfo.color}`}>
                                {statusInfo.label}
                              </span>
                              {!isRegular && (
                                <>
                                  <span
                                    className={`absolute -top-0.5 -right-0.5 inline-block w-2 h-2 rounded-full ${dotCls} ring-2 ring-white shadow-sm`}
                                    aria-label={type}
                                  />
                                  <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 z-30 hidden group-hover:block">
                                    <div className="bg-gray-900 text-white text-xs rounded-md shadow-lg px-3 py-2 whitespace-normal w-64 text-left space-y-1">
                                      <div className="font-semibold text-sm">{category}</div>
                                      {approvalState && (
                                        <div className="text-gray-300">
                                          Approval: <span className="text-white font-medium">{approvalState}</span>
                                        </div>
                                      )}
                                      {requestSubtype && (
                                        <div className="text-gray-300">
                                          Request: <span className="text-white">{requestSubtype}</span>
                                        </div>
                                      )}
                                      {hasLeaveType && (
                                        <div className="text-gray-300">
                                          Leave Type: <span className="text-white">{record.leaveType}</span>
                                        </div>
                                      )}
                                      {record.regularizationComments && (
                                        <div className="text-gray-300 break-words">
                                          Reason: <span className="text-white">{record.regularizationComments}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {record.statusLabel || statusInfo.label}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-center">
                        {record.signInLocation ? (
                          <a
                            href={`https://www.google.com/maps?q=${record.signInLocation}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            View Map
                          </a>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-center">
                        {record.signOutLocation ? (
                          <a
                            href={`https://www.google.com/maps?q=${record.signOutLocation}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            View Map
                          </a>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-center">
                        {signInSelfieUrl ? (
                          <div className="flex justify-center">
                            <button
                              onClick={() => setLightboxUrl(signInSelfieUrl)}
                              className="block"
                            >
                              <img
                                src={signInSelfieUrl}
                                alt="Sign-in selfie"
                                className="w-12 h-12 rounded-full object-cover border-2 border-gray-200 hover:border-blue-500 transition-colors cursor-pointer"
                                style={{ imageOrientation: 'from-image' }}
                                onError={(e) => {
                                  const parent = e.currentTarget.parentElement?.parentElement;
                                  if (parent) {
                                    parent.innerHTML = '<span class="text-gray-400 text-xs">—</span>';
                                  }
                                }}
                              />
                            </button>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-center">
                        {signOutSelfieUrl ? (
                          <div className="flex justify-center">
                            <button
                              onClick={() => setLightboxUrl(signOutSelfieUrl)}
                              className="block"
                            >
                              <img
                                src={signOutSelfieUrl}
                                alt="Sign-out selfie"
                                className="w-12 h-12 rounded-full object-cover border-2 border-gray-200 hover:border-blue-500 transition-colors cursor-pointer"
                                style={{ imageOrientation: 'from-image' }}
                                onError={(e) => {
                                  const parent = e.currentTarget.parentElement?.parentElement;
                                  if (parent) {
                                    parent.innerHTML = '<span class="text-gray-400 text-xs">—</span>';
                                  }
                                }}
                              />
                            </button>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
        </div>
        )}

        {/* 3-column footer: rows-per-page left, page nav truly centered,
            empty spacer right so nothing falls under the fixed SSI /
            "New custom report" widget at bottom-right. */}
        {!loading && filteredData.length > 0 && (
          <PaginationFooter
            safePage={safePage}
            totalPages={totalPages}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            totalRows={filteredData.length}
            onPageSizeChange={(value) => setPageSize(value)}
            onPrevPage={() => setCurrentPage((p) => Math.max(1, p - 1))}
            onNextPage={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          />
        )}
      <DashboardFilterModal
        open={storeFiltersOpen}
        onClose={() => setStoreFiltersOpen(false)}
        title="Filters"
        sections={storeFilters.sections}
        activeCount={storeFilters.activeCount}
        onClearAll={storeFilters.clear}
      />

      <ImageLightbox
        src={lightboxUrl || ''}
        alt="Attendance Selfie"
        isOpen={!!lightboxUrl}
        onClose={() => setLightboxUrl(null)}
      />
    </ReportPageScaffold>
    </CustomReportPanel>
  );
}
